// page/business/BusinessShopPage.tsx
// Business-facing shop dashboard
// Shows: incoming orders (with buyer shipping address), earnings, listings
// Business handles shipping themselves — they CANNOT process payouts

import React, { useEffect, useState, useCallback } from 'react';
import {
  Package, Truck, DollarSign, ShoppingBag, CheckCircle,
  Clock, AlertTriangle, Eye, ChevronDown, ChevronUp,
  Copy, ExternalLink, RefreshCw, MapPin, Phone, Mail,
  X, TrendingUp, BarChart2
} from 'lucide-react';

// ─── API ─────────────────────────────────────────────────────────────────────
const API = process.env.REACT_APP_API_URL || 'http://localhost:3000/api';

function authHeaders(): Record<string, string> {
  const token =
    localStorage.getItem('businessToken') ||
    localStorage.getItem('token') || '';
  return { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };
}

// ─── Types ────────────────────────────────────────────────────────────────────
interface ShippingAddress {
  fullName:    string;
  phone:       string;
  email:       string;
  addressLine1:string;
  addressLine2?:string;
  city:        string;
  state:       string;
  postalCode:  string;
  country:     string;
}

interface OrderItem {
  listingId:   string;
  productName: string;
  quantity:    number;
  unitPrice:   number;
  currency:    string;
}

interface Order {
  _id:             string;
  orderId:         string;
  status:          'pending' | 'processing' | 'shipped' | 'completed' | 'disputed' | 'cancelled';
  items:           OrderItem[];
  subtotal:        number;
  currency:        string;
  shippingAddress: ShippingAddress;
  trackingNumber?: string;
  carrier?:        string;
  notes?:          string;
  createdAt:       string;
  updatedAt:       string;
}

interface EarningsSummary {
  grossSales:         number;
  platformFee:        number;
  netEarnings:        number;
  pendingPayout:      number;
  totalPaidOut:       number;
  ordersThisMonth:    number;
  currency:           string;
}

interface Listing {
  _id:         string;
  listingId:   string;
  name:        string;
  country:     string;
  price:       number;
  currency:    string;
  status:      string;
  isActive:    boolean;
  shopStats:   { totalViews: number; totalOrders: number; totalRevenue: number };
  isFeatured:  boolean;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────
const fmt = {
  cur: (v: number, cur = 'USD') =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: cur, maximumFractionDigits: 0 }).format(v),
  date: (d: string) => new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' }),
  short: (d: string) => new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
};

const FLAGS: Record<string, string> = {
  US:'🇺🇸', VN:'🇻🇳', TH:'🇹🇭', SG:'🇸🇬', AU:'🇦🇺', CN:'🇨🇳', GL:'🌐',
};

const STATUS_CONFIG: Record<string, { label: string; color: string; bg: string }> = {
  pending:    { label: 'Pending',    color: 'text-orange-700', bg: 'bg-orange-100' },
  processing: { label: 'Processing', color: 'text-yellow-700', bg: 'bg-yellow-100' },
  shipped:    { label: 'Shipped',    color: 'text-blue-700',   bg: 'bg-blue-100'   },
  completed:  { label: 'Completed',  color: 'text-green-700',  bg: 'bg-green-100'  },
  disputed:   { label: 'Disputed',   color: 'text-red-700',    bg: 'bg-red-100'    },
  cancelled:  { label: 'Cancelled',  color: 'text-gray-600',   bg: 'bg-gray-100'   },
};

// ─── Order Row Component ──────────────────────────────────────────────────────
const OrderRow: React.FC<{
  order: Order;
  onMarkShipped: (orderId: string, tracking: string, carrier: string) => Promise<void>;
}> = ({ order, onMarkShipped }) => {
  const [expanded,    setExpanded]    = useState(false);
  const [shipping,    setShipping]    = useState(false);
  const [tracking,    setTracking]    = useState('');
  const [carrier,     setCarrier]     = useState('');
  const [copied,      setCopied]      = useState(false);
  const [saving,      setSaving]      = useState(false);

  const cfg = STATUS_CONFIG[order.status] || STATUS_CONFIG.pending;
  const addr = order.shippingAddress;
  const canShip = order.status === 'processing' || order.status === 'pending';

  const copyAddress = () => {
    const text = `${addr.fullName}\n${addr.addressLine1}${addr.addressLine2 ? '\n' + addr.addressLine2 : ''}\n${addr.city}, ${addr.state} ${addr.postalCode}\n${addr.country}\n📞 ${addr.phone}\n📧 ${addr.email}`;
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleMarkShipped = async () => {
    if (!tracking.trim()) return;
    setSaving(true);
    try {
      await onMarkShipped(order._id, tracking, carrier);
      setShipping(false);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden">

      {/* Order header row */}
      <div
        className="flex items-center justify-between px-5 py-4 bg-white dark:bg-gray-800 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center gap-4 flex-wrap">
          <span className="font-mono text-sm font-bold text-blue-600 dark:text-blue-400">{order.orderId}</span>
          <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${cfg.bg} ${cfg.color}`}>
            {cfg.label}
          </span>
          <div className="text-sm text-gray-600 dark:text-gray-300">
            {order.items.map(i => i.productName).join(', ')}
          </div>
        </div>
        <div className="flex items-center gap-4 flex-shrink-0">
          <span className="text-sm font-bold text-gray-900 dark:text-white">
            {fmt.cur(order.subtotal, order.currency)}
          </span>
          <span className="text-xs text-gray-400 dark:text-gray-500 hidden sm:block">
            {fmt.short(order.createdAt)}
          </span>
          {expanded
            ? <ChevronUp className="w-4 h-4 text-gray-400" />
            : <ChevronDown className="w-4 h-4 text-gray-400" />
          }
        </div>
      </div>

      {/* Expanded details */}
      {expanded && (
        <div className="bg-gray-50 dark:bg-gray-700/30 border-t border-gray-200 dark:border-gray-700 p-5 space-y-5">

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">

            {/* Shipping Address — most important for business */}
            <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-blue-500" />
                  <h4 className="text-sm font-semibold text-gray-900 dark:text-white">Ship To</h4>
                </div>
                <button
                  onClick={copyAddress}
                  className="text-xs text-blue-600 dark:text-blue-400 hover:underline flex items-center gap-1"
                >
                  <Copy className="w-3 h-3" />
                  {copied ? 'Copied!' : 'Copy'}
                </button>
              </div>
              <div className="space-y-1.5 text-sm">
                <p className="font-semibold text-gray-900 dark:text-white text-base">{addr.fullName}</p>
                <p className="text-gray-600 dark:text-gray-300">{addr.addressLine1}</p>
                {addr.addressLine2 && <p className="text-gray-600 dark:text-gray-300">{addr.addressLine2}</p>}
                <p className="text-gray-600 dark:text-gray-300">
                  {addr.city}, {addr.state} {addr.postalCode}
                </p>
                <p className="text-gray-600 dark:text-gray-300 font-medium">{addr.country}</p>
                <div className="pt-2 space-y-1 border-t border-gray-100 dark:border-gray-700">
                  <p className="flex items-center gap-2 text-gray-600 dark:text-gray-300">
                    <Phone className="w-3.5 h-3.5 text-gray-400" />{addr.phone}
                  </p>
                  <p className="flex items-center gap-2 text-gray-600 dark:text-gray-300">
                    <Mail className="w-3.5 h-3.5 text-gray-400" />{addr.email}
                  </p>
                </div>
              </div>
            </div>

            {/* Order Items + Earnings breakdown */}
            <div className="space-y-3">
              {/* Items */}
              <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-700">
                <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                  <Package className="w-4 h-4 text-purple-500" /> Items Ordered
                </h4>
                <div className="space-y-2">
                  {order.items.map((item, i) => (
                    <div key={i} className="flex items-center justify-between text-sm">
                      <span className="text-gray-700 dark:text-gray-300">
                        {item.productName}
                        <span className="text-gray-400 ml-1">×{item.quantity}</span>
                      </span>
                      <span className="font-semibold text-gray-900 dark:text-white">
                        {fmt.cur(item.unitPrice * item.quantity, item.currency)}
                      </span>
                    </div>
                  ))}
                  <div className="flex items-center justify-between text-sm font-bold border-t border-gray-100 dark:border-gray-700 pt-2">
                    <span className="text-gray-900 dark:text-white">Subtotal</span>
                    <span className="text-gray-900 dark:text-white">{fmt.cur(order.subtotal, order.currency)}</span>
                  </div>
                  <div className="flex items-center justify-between text-xs text-orange-600 dark:text-orange-400">
                    <span>JepTags platform fee (5%)</span>
                    <span>- {fmt.cur(order.subtotal * 0.05, order.currency)}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm font-bold text-green-600 dark:text-green-400">
                    <span>Your net earnings</span>
                    <span>{fmt.cur(order.subtotal * 0.95, order.currency)}</span>
                  </div>
                </div>
              </div>

              {/* Tracking (if already shipped) */}
              {order.trackingNumber && (
                <div className="bg-blue-50 dark:bg-blue-900/20 rounded-xl p-3 border border-blue-200 dark:border-blue-800 text-sm">
                  <div className="flex items-center gap-2 text-blue-700 dark:text-blue-400">
                    <Truck className="w-4 h-4" />
                    <span className="font-semibold">Tracking: {order.trackingNumber}</span>
                    {order.carrier && <span className="text-blue-500">via {order.carrier}</span>}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Mark as Shipped action */}
          {canShip && (
            <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-700">
              {!shipping ? (
                <button
                  onClick={() => setShipping(true)}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors"
                >
                  <Truck className="w-4 h-4" />
                  Mark as Shipped
                </button>
              ) : (
                <div className="space-y-3">
                  <h4 className="text-sm font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                    <Truck className="w-4 h-4 text-blue-500" /> Enter Shipping Details
                  </h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs text-gray-500 dark:text-gray-400 block mb-1">Tracking Number *</label>
                      <input
                        type="text"
                        value={tracking}
                        onChange={e => setTracking(e.target.value)}
                        placeholder="e.g. 1Z999AA10123456784"
                        className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm dark:bg-gray-700 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-gray-500 dark:text-gray-400 block mb-1">Carrier</label>
                      <select
                        value={carrier}
                        onChange={e => setCarrier(e.target.value)}
                        className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 dark:text-white"
                      >
                        <option value="">Select carrier...</option>
                        <option value="DHL">DHL</option>
                        <option value="FedEx">FedEx</option>
                        <option value="UPS">UPS</option>
                        <option value="USPS">USPS</option>
                        <option value="ViettelPost">ViettelPost</option>
                        <option value="VNPost">VNPost</option>
                        <option value="GHN">GHN (Vietnam)</option>
                        <option value="GHTK">GHTK (Vietnam)</option>
                        <option value="ThaiPost">Thailand Post</option>
                        <option value="SingPost">SingPost</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={handleMarkShipped}
                      disabled={!tracking.trim() || saving}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50 transition-colors flex items-center gap-2"
                    >
                      {saving ? <><RefreshCw className="w-3.5 h-3.5 animate-spin" /> Saving...</> : <><CheckCircle className="w-3.5 h-3.5" /> Confirm Shipped</>}
                    </button>
                    <button
                      onClick={() => { setShipping(false); setTracking(''); setCarrier(''); }}
                      className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg text-sm hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                  <p className="text-xs text-gray-400 dark:text-gray-500">
                    ℹ️ Once marked as shipped, the buyer will be notified. JepTags will release your payment after delivery confirmation.
                  </p>
                </div>
              )}
            </div>
          )}

          <p className="text-xs text-gray-400 dark:text-gray-500">
            Order placed: {fmt.date(order.createdAt)} · Last updated: {fmt.date(order.updatedAt)}
          </p>
        </div>
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// MAIN PAGE
// ═══════════════════════════════════════════════════════════════════════════
const BusinessShopPage: React.FC = () => {
  const [tab,      setTab]      = useState<'orders' | 'earnings' | 'listings'>('orders');
  const [orders,   setOrders]   = useState<Order[]>([]);
  const [earnings, setEarnings] = useState<EarningsSummary | null>(null);
  const [listings, setListings] = useState<Listing[]>([]);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState('');
  const [filter,   setFilter]   = useState('');
  const [toast,    setToast]    = useState('');

  const showToast = (msg: string) => { setToast(msg); setTimeout(() => setToast(''), 3000); };

  // ── Fetch orders ────────────────────────────────────────────────────────
  const loadOrders = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const qs = filter ? `?status=${filter}` : '';
      const res  = await fetch(`${API}/shop/orders/manage/my-orders${qs}`, { headers: authHeaders() });
      const data = await res.json();
      setOrders(data.data?.orders || data.orders || []);
    } catch (e: any) { setError(e.message); }
    setLoading(false);
  }, [filter]);

  // ── Fetch earnings ───────────────────────────────────────────────────────
  const loadEarnings = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const res  = await fetch(`${API}/shop/orders/manage/my-earnings`, { headers: authHeaders() });
      const data = await res.json();
      setEarnings(data.data || data);
    } catch (e: any) { setError(e.message); }
    setLoading(false);
  }, []);

  // ── Fetch listings ───────────────────────────────────────────────────────
  const loadListings = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const res  = await fetch(`${API}/shop/listings?myListings=true`, { headers: authHeaders() });
      const data = await res.json();
      setListings(data.data?.listings || data.listings || []);
    } catch (e: any) { setError(e.message); }
    setLoading(false);
  }, []);

  useEffect(() => {
    if (tab === 'orders')   loadOrders();
    if (tab === 'earnings') loadEarnings();
    if (tab === 'listings') loadListings();
  }, [tab, loadOrders, loadEarnings, loadListings]);

  // ── Mark as shipped ──────────────────────────────────────────────────────
  const markShipped = async (orderId: string, trackingNumber: string, carrier: string) => {
    const res  = await fetch(`${API}/shop/orders/manage/${orderId}/ship`, {
      method: 'PUT',
      headers: authHeaders(),
      body: JSON.stringify({ trackingNumber, carrier }),
    });
    const data = await res.json();
    if (data.success) {
      showToast('✓ Order marked as shipped — buyer notified!');
      loadOrders();
    } else {
      throw new Error(data.message || 'Failed to update');
    }
  };

  // ── Stats from orders ─────────────────────────────────────────────────────
  const orderStats = {
    all:        orders.length,
    newOrders:  orders.filter(o => o.status === 'processing' || o.status === 'pending').length,
    shipped:    orders.filter(o => o.status === 'shipped').length,
    completed:  orders.filter(o => o.status === 'completed').length,
    disputed:   orders.filter(o => o.status === 'disputed').length,
  };

  const filteredOrders = filter ? orders.filter(o => o.status === filter) : orders;

  return (
    <div className="space-y-5">

      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">My Shop</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
            Manage your orders, track earnings, and view your listings on JepTags Shop
          </p>
        </div>
        <a
          href="https://shop.jeptags.com"
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-2 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
        >
          <ExternalLink className="w-4 h-4" />
          View My Store
        </a>
      </div>

      {/* Tabs */}
      <div className="flex space-x-1 border-b border-gray-200 dark:border-gray-700">
        {[
          { key: 'orders',   label: 'Orders',   icon: Package,   badge: orderStats.newOrders > 0 ? orderStats.newOrders : null },
          { key: 'earnings', label: 'Earnings', icon: DollarSign, badge: null },
          { key: 'listings', label: 'Listings', icon: ShoppingBag, badge: null },
        ].map(({ key, label, icon: Icon, badge }) => (
          <button
            key={key}
            onClick={() => setTab(key as any)}
            className={`flex items-center gap-2 px-5 py-3 text-sm font-medium border-b-2 transition-colors ${
              tab === key
                ? 'border-blue-600 text-blue-600 dark:text-blue-400'
                : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
            }`}
          >
            <Icon className="w-4 h-4" />
            {label}
            {badge != null && badge > 0 && (
              <span className="px-1.5 py-0.5 text-xs font-bold bg-red-500 text-white rounded-full animate-pulse">
                {badge}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Error */}
      {error && (
        <div className="flex items-center justify-between p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl text-sm">
          <span className="text-red-700 dark:text-red-400">{error}</span>
          <button onClick={() => { if (tab==='orders') loadOrders(); else if (tab==='earnings') loadEarnings(); else loadListings(); }}
            className="text-xs text-red-600 underline ml-3">Retry</button>
        </div>
      )}

      {/* ── ORDERS TAB ─────────────────────────────────────────────────────── */}
      {tab === 'orders' && (
        <div className="space-y-4">
          {/* Order stat pills */}
          <div className="flex flex-wrap gap-2">
            {[
              { key: '',           label: `All (${orderStats.all})` },
              { key: 'pending',    label: `Pending (${orders.filter(o=>o.status==='pending').length})` },
              { key: 'processing', label: `New Orders (${orders.filter(o=>o.status==='processing').length})` },
              { key: 'shipped',    label: `Shipped (${orderStats.shipped})` },
              { key: 'completed',  label: `Completed (${orderStats.completed})` },
              { key: 'disputed',   label: `Disputed (${orderStats.disputed})` },
            ].map(f => (
              <button key={f.key} onClick={() => setFilter(f.key)}
                className={`px-3 py-1.5 text-xs font-medium rounded-full border transition-colors ${
                  filter === f.key
                    ? 'bg-blue-600 text-white border-blue-600'
                    : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:border-blue-400'
                }`}>
                {f.label}
              </button>
            ))}
            <button onClick={loadOrders} className="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-full">
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>

          {/* New order alert */}
          {orderStats.newOrders > 0 && (
            <div className="flex items-start gap-3 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl">
              <AlertTriangle className="w-5 h-5 text-blue-500 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm font-semibold text-blue-800 dark:text-blue-300">
                  {orderStats.newOrders} new order{orderStats.newOrders > 1 ? 's' : ''} waiting to ship!
                </p>
                <p className="text-xs text-blue-600 dark:text-blue-400 mt-0.5">
                  Click on each order to see the buyer's shipping address and mark as shipped.
                </p>
              </div>
            </div>
          )}

          {/* Orders list */}
          {loading ? (
            <div className="text-center py-12">
              <RefreshCw className="w-7 h-7 text-blue-500 animate-spin mx-auto mb-3" />
              <p className="text-sm text-gray-400 dark:text-gray-500">Loading orders...</p>
            </div>
          ) : filteredOrders.length === 0 ? (
            <div className="text-center py-12 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700">
              <Package className="w-10 h-10 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400">No orders yet</p>
              <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">Orders will appear here when buyers purchase your products</p>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredOrders.map(order => (
                <OrderRow key={order._id} order={order} onMarkShipped={markShipped} />
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── EARNINGS TAB ───────────────────────────────────────────────────── */}
      {tab === 'earnings' && (
        <div className="space-y-5">
          {loading ? (
            <div className="text-center py-12"><RefreshCw className="w-7 h-7 text-blue-500 animate-spin mx-auto" /></div>
          ) : earnings ? (
            <>
              {/* Summary cards */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                {[
                  { label: 'Gross Sales',     value: fmt.cur(earnings.grossSales, earnings.currency),    color: 'text-gray-900 dark:text-white',          sub: 'Total orders value' },
                  { label: 'Platform Fee',    value: `- ${fmt.cur(earnings.platformFee, earnings.currency)}`, color: 'text-orange-600 dark:text-orange-400', sub: '5% of gross sales' },
                  { label: 'Net Earnings',    value: fmt.cur(earnings.netEarnings, earnings.currency),   color: 'text-green-600 dark:text-green-400',     sub: 'After JepTags fee' },
                  { label: 'Pending Payout',  value: fmt.cur(earnings.pendingPayout, earnings.currency), color: 'text-blue-600 dark:text-blue-400',       sub: 'JepTags will pay you' },
                ].map(({ label, value, color, sub }) => (
                  <div key={label} className="bg-white dark:bg-gray-800 rounded-xl p-5 border border-gray-200 dark:border-gray-700">
                    <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">{label}</p>
                    <p className={`text-2xl font-bold mt-1 ${color}`}>{value}</p>
                    <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">{sub}</p>
                  </div>
                ))}
              </div>

              {/* Payout info box */}
              <div className="bg-white dark:bg-gray-800 rounded-xl p-5 border border-gray-200 dark:border-gray-700">
                <h3 className="font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                  <DollarSign className="w-4 h-4 text-green-500" />
                  Payout Information
                </h3>
                <div className="space-y-3 text-sm text-gray-600 dark:text-gray-300">
                  <div className="flex items-start gap-3 p-3 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
                    <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="font-medium text-green-800 dark:text-green-300">Total Paid Out: {fmt.cur(earnings.totalPaidOut, earnings.currency)}</p>
                      <p className="text-green-600 dark:text-green-400 text-xs mt-0.5">Already transferred to your bank account</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                    <Clock className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="font-medium text-blue-800 dark:text-blue-300">Pending: {fmt.cur(earnings.pendingPayout, earnings.currency)}</p>
                      <p className="text-blue-600 dark:text-blue-400 text-xs mt-0.5">Will be paid every Friday after buyer confirms delivery</p>
                    </div>
                  </div>
                  <p className="text-xs text-gray-400 dark:text-gray-500 pt-1">
                    ℹ️ JepTags processes payouts every Friday. Funds are released after the buyer confirms delivery or 7 days after shipping, whichever comes first.
                  </p>
                </div>
              </div>

              <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-700 flex items-center justify-between">
                <div className="text-sm text-gray-600 dark:text-gray-300">
                  <span className="font-medium">Orders this month:</span> {earnings.ordersThisMonth}
                </div>
                <div className="text-xs text-gray-400 dark:text-gray-500">
                  Contact <span className="text-blue-600 dark:text-blue-400">finance@jeptags.com</span> for payout queries
                </div>
              </div>
            </>
          ) : (
            <div className="text-center py-12 text-gray-400 dark:text-gray-500">
              <BarChart2 className="w-10 h-10 mx-auto mb-3 text-gray-300 dark:text-gray-600" />
              No earnings data yet
            </div>
          )}
        </div>
      )}

      {/* ── LISTINGS TAB ───────────────────────────────────────────────────── */}
      {tab === 'listings' && (
        <div className="space-y-4">
          {loading ? (
            <div className="text-center py-12"><RefreshCw className="w-7 h-7 text-blue-500 animate-spin mx-auto" /></div>
          ) : listings.length === 0 ? (
            <div className="text-center py-12 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700">
              <ShoppingBag className="w-10 h-10 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400">No listings yet</p>
            </div>
          ) : (
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 dark:bg-gray-700/50 border-b border-gray-200 dark:border-gray-700">
                    <tr>
                      {['Product', 'Country', 'Price', 'Status', 'Views', 'Orders', 'Revenue'].map(h => (
                        <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                    {listings.map(l => (
                      <tr key={l._id} className="hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors">
                        <td className="px-4 py-3">
                          <div className="font-medium text-gray-900 dark:text-white truncate max-w-[180px]">{l.name}</div>
                          {l.isFeatured && <span className="text-xs text-yellow-600">⭐ Featured</span>}
                        </td>
                        <td className="px-4 py-3 text-gray-600 dark:text-gray-300">
                          {FLAGS[l.country] || '🌐'} {l.country}
                        </td>
                        <td className="px-4 py-3 font-semibold font-mono text-gray-900 dark:text-white">
                          {l.price.toLocaleString()} {l.currency}
                        </td>
                        <td className="px-4 py-3">
                          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                            l.isActive && l.status === 'active'
                              ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                              : l.status === 'pending'
                                ? 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400'
                                : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300'
                          }`}>
                            {l.isActive && l.status === 'active' ? '● Live' : l.status}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-gray-600 dark:text-gray-300 font-mono">
                          {(l.shopStats?.totalViews || 0).toLocaleString()}
                        </td>
                        <td className="px-4 py-3 font-mono text-gray-900 dark:text-white font-semibold">
                          {(l.shopStats?.totalOrders || 0).toLocaleString()}
                        </td>
                        <td className="px-4 py-3 font-mono text-green-600 dark:text-green-400 font-semibold">
                          {(l.shopStats?.totalRevenue || 0).toLocaleString()} {l.currency}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div className="fixed bottom-6 right-6 bg-gray-900 dark:bg-gray-700 text-white px-4 py-3 rounded-xl shadow-xl text-sm font-medium z-50 flex items-center gap-2">
          <CheckCircle className="w-4 h-4 text-green-400" />
          {toast}
        </div>
      )}
    </div>
  );
};

export default BusinessShopPage;
